import React, { useEffect, useRef } from 'react';

const ConfirmModal = ({
  isOpen,
  title = 'Are you sure?',
  message = 'This action cannot be undone.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isProcessing = false,
  tone = 'danger' // 'danger' | 'default'
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    const onClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const toneClasses = tone === 'danger'
    ? {
        headerIconBg: 'bg-red-100',
        headerIconText: 'text-red-600',
        confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        confirmText: 'text-white'
      }
    : {
        headerIconBg: 'bg-gray-100',
        headerIconText: 'text-gray-600',
        confirmBg: 'bg-gray-800 hover:bg-black focus:ring-gray-500',
        confirmText: 'text-white'
      };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      {/* Bottom sheet on mobile, centered modal on larger screens */}
      <div
        ref={modalRef}
        className="w-full sm:max-w-md sm:rounded-2xl bg-white shadow-2xl transition-all duration-300 ease-out rounded-t-2xl sm:rounded-t-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${toneClasses.headerIconBg} ${toneClasses.headerIconText}`}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-.75 6a.75.75 0 011.5 0v6a.75.75 0 01-1.5 0V8.25zM12 16.5a.75.75 0 100 1.5.75.75 0 000-1.5z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 id="confirm-modal-title" className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm sm:text-base text-gray-600">{message}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="inline-flex justify-center items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isProcessing}
              className={`inline-flex justify-center items-center rounded-lg px-4 py-2 text-sm font-semibold ${toneClasses.confirmText} ${toneClasses.confirmBg} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isProcessing && (
                <svg className="-ml-1 mr-2 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
              )}
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;


